/**
 * Rules shared by the server-side filter (PlayerMemory) and the client / bot reducer
 * (PlayerView), so both sides agree on ghosts and expiry without extra messages.
 */
import { TICK_HZ, type EntityKind, type EntityView, type Ghost, type Tick } from '@sa/sim';
import { qAngle, qPos } from '../quant.js';

/** Moving entities resend their motion segment at least this often (§3: 1 s heartbeat). */
export const HEARTBEAT_TICKS = 10;
/** Pop / radiation nibbles go out at 2 Hz. */
export const POP_RAD_EVERY = 5;
/** Area shares go out at 1 Hz. */
export const SHARES_EVERY = 10;

/** How long transient knowledge stays in a PlayerView / PlayerMemory (ticks). */
export const VIEW_TTL = {
  /** ghosts of mobile units (aircraft, ships, bombs) */
  mobileGhost: 600,
  /** back-plotted launch-origin contact (§2: 60 s ghost silo / sub) */
  originContact: 600,
  /** "LAUNCH DETECTED / NO TRACK" wedges */
  hint: 300,
  detonation: 100,
  /** resolved intercepts (pending ones stay until resolved or this long past resolveTick) */
  intercept: 50,
  phase: 300,
  /** tracks are dropped this long after their impact tick if no remove arrived */
  trackGrace: 10,
} as const;

const MOBILE: ReadonlySet<EntityKind> = new Set<EntityKind>(['fighter', 'bomber', 'sub', 'carrier', 'bomb']);

export const isMobileKind = (k: EntityKind): boolean => MOBILE.has(k);

/** Kinds that travel as entity upserts (missiles travel as tracks, interceptors as intercept events). */
export const isWireKind = (k: EntityKind): boolean => k !== 'icbm' && k !== 'slbm' && k !== 'interceptor';

/** A mobile ghost has expired (structures never expire; they are cleared by observation). */
export const ghostExpired = (g: Ghost, tick: Tick): boolean => isMobileKind(g.kind) && tick - g.tick > VIEW_TTL.mobileGhost;

/**
 * Last-known record of an entity view that left vision, evaluated at `tick` (the last tick
 * it was seen). Quantised so the server memory and the reducer build identical ghosts.
 */
export function ghostFromView(v: EntityView, tick: Tick): Ghost {
  const moving = v.vx !== 0 || v.vy !== 0;
  const dt = (tick - v.segTick) / TICK_HZ;
  const g: Ghost = {
    id: v.id,
    kind: v.kind,
    owner: v.owner,
    x: moving ? qPos(v.x + v.vx * dt) : v.x,
    y: moving ? qPos(v.y + v.vy * dt) : v.y,
    tick,
  };
  if (moving) g.heading = qAngle(Math.atan2(v.vy, v.vx));
  if (v.extra !== 0) g.extra = v.extra;
  return g;
}
